import React, { useState, useEffect } from 'react';
import {
  Boxes,
  RotateCcw,
  Check,
  Send,
  HelpCircle,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { SessionGameRound, SessionPlayerState } from '../../../types/session-games';
import { validateSentenceAnswer } from '../../../services/sessionGameService';
import { playCorrectSound, playWrongSound } from '../../../utils/soundEffects';

interface SentenceArchitectViewProps {
  round: SessionGameRound;
  myPlayer: SessionPlayerState;
  partnerPlayer?: SessionPlayerState;
  onSubmit: (result: {
    answer: string[];
    isCorrect: boolean;
    points: number;
  }) => void;
  isRoundResult: boolean;
}

export const SentenceArchitectView: React.FC<SentenceArchitectViewProps> = ({
  round,
  myPlayer,
  partnerPlayer,
  onSubmit,
  isRoundResult
}) => {
  const [wordBank, setWordBank] = useState<string[]>([]);
  const [assembled, setAssembled] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Initialize or reset word bank on round change
  useEffect(() => {
    if (round.scrambledWords) {
      setWordBank([...round.scrambledWords]);
    } else {
      setWordBank(round.correctAnswer.split(' ').sort(() => Math.random() - 0.5));
    }
    setAssembled([]);
    setFeedback(null);
    setSubmitted(false);
  }, [round.id]);

  const handleWordSelect = (word: string, indexInBank: number) => {
    if (submitted || isRoundResult) return;
    setAssembled((prev) => [...prev, word]);
    setWordBank((prev) => prev.filter((_, idx) => idx !== indexInBank));
    setFeedback(null);
  };

  const handleWordRemove = (word: string, indexInAssembled: number) => {
    if (submitted || isRoundResult) return;
    setWordBank((prev) => [...prev, word]);
    setAssembled((prev) => prev.filter((_, idx) => idx !== indexInAssembled));
    setFeedback(null);
  };

  const handleReset = () => {
    if (submitted || isRoundResult) return;
    if (round.scrambledWords) {
      setWordBank([...round.scrambledWords]);
    } else {
      setWordBank(round.correctAnswer.split(' ').sort(() => Math.random() - 0.5));
    }
    setAssembled([]);
    setFeedback(null);
  };

  const handleCheck = () => {
    if (assembled.length === 0 || submitted) return;
    const isCorrect = validateSentenceAnswer(round.correctAnswer, assembled);

    if (isCorrect) {
      playCorrectSound();
      setFeedback('correct');
      setSubmitted(true);
      onSubmit({
        answer: assembled,
        isCorrect: true,
        points: 20
      });
    } else {
      playWrongSound();
      setFeedback('incorrect');
    }
  };

  return (
    <div className="flex flex-col items-center justify-between h-full max-w-2xl mx-auto space-y-6">
      {/* Header Info & Translation Hint */}
      <div className="w-full bg-white rounded-3xl p-5 md:p-6 border border-blue-100 shadow-md text-center">
        <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">
          <Boxes className="w-4 h-4" />
          <span>Reconstruct the Sentence</span>
        </div>

        {round.translation && (
          <p className="text-sm md:text-base text-gray-800 font-semibold italic">
            "{round.translation}"
          </p>
        )}
      </div>

      {/* Assembly Bar */}
      <div className="w-full min-h-[110px] bg-blue-50/50 rounded-2xl p-4 border-2 border-dashed border-blue-300 flex flex-wrap items-center justify-center gap-2 transition-all">
        {assembled.length === 0 ? (
          <p className="text-xs md:text-sm text-blue-400 font-medium">
            Tap the word chips below to build the sentence in correct order
          </p>
        ) : (
          assembled.map((word, idx) => (
            <button
              key={`${word}_${idx}`}
              onClick={() => handleWordRemove(word, idx)}
              disabled={submitted || isRoundResult}
              className="px-3.5 py-2 rounded-xl bg-blue-600 text-white font-bold text-sm shadow-xs hover:bg-blue-700 transition-all transform active:scale-95 flex items-center gap-1 group"
            >
              <span>{word}</span>
              <span className="text-blue-200 text-xs group-hover:text-white">✕</span>
            </button>
          ))
        )}
      </div>

      {/* Feedback banner */}
      {feedback === 'correct' && (
        <div className="w-full bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl p-3 flex items-center justify-center gap-2 text-xs font-bold animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span>Perfect sentence structure! +20 points earned.</span>
        </div>
      )}

      {feedback === 'incorrect' && (
        <div className="w-full bg-red-50 border border-red-200 text-red-800 rounded-2xl p-3 flex items-center justify-center gap-2 text-xs font-bold animate-in shake">
          <XCircle className="w-4 h-4 text-red-600" />
          <span>Not quite right yet. Rearrange words or tap ✕ to remove!</span>
        </div>
      )}

      {/* Word Bank */}
      <div className="w-full bg-white rounded-2xl p-4 border border-gray-100 shadow-xs">
        <div className="flex items-center justify-between pb-2 border-b border-gray-100 mb-3 text-xs text-gray-400 font-bold uppercase tracking-wider">
          <span>Available Word Bank:</span>
          <button
            onClick={handleReset}
            disabled={submitted || isRoundResult}
            className="flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Reset</span>
          </button>
        </div>

        <div className="flex flex-wrap gap-2.5 justify-center min-h-[50px] items-center">
          {wordBank.map((word, idx) => (
            <button
              key={`${word}_${idx}`}
              onClick={() => handleWordSelect(word, idx)}
              disabled={submitted || isRoundResult}
              className="px-3.5 py-2 rounded-xl bg-gray-100 hover:bg-blue-100 hover:text-blue-700 text-gray-800 font-bold text-sm border border-gray-200 shadow-xs transition-all transform active:scale-95"
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {/* Action Button */}
      {!submitted && !isRoundResult && (
        <button
          onClick={handleCheck}
          disabled={assembled.length === 0}
          className="w-full max-w-sm py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-black text-sm shadow-md hover:opacity-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <Check className="w-4 h-4" />
          <span>Check Sentence</span>
        </button>
      )}

      {/* Partner Status Card */}
      {partnerPlayer && (
        <div className="w-full bg-white rounded-2xl p-3 border border-gray-100 shadow-xs flex items-center justify-between text-xs">
          <span className="font-bold text-gray-800">{partnerPlayer.userName}:</span>
          <span className={partnerPlayer.roundFinished ? 'text-emerald-600 font-bold' : 'text-gray-400'}>
            {partnerPlayer.roundFinished ? '✓ Sentence Solved!' : 'Building sentence...'}
          </span>
        </div>
      )}
    </div>
  );
};
